import React from "react";
import { Button } from "./button";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { commonPaginationLabels, commonMiscLabels } from "@/i18n";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  itemsPerPageOptions?: number[];
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 25, 50],
}) => {
  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const visiblePages = getVisiblePages();

  // i18n labels
  const paginationLabels = commonPaginationLabels();
  const miscLabels = commonMiscLabels();

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mt-3 sm:mt-4 p-2 sm:p-3 bg-card backdrop-blur border rounded-md">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground order-1 sm:order-1">
        <span className="hidden sm:inline">{paginationLabels.show}</span>
        <Select
          nativeSheetTitle={paginationLabels.show}
          value={itemsPerPage.toString()}
          onValueChange={(value) => onItemsPerPageChange(Number(value))}
        >
          <SelectTrigger className="w-14 sm:w-18 h-6 sm:h-7 text-xs border-muted">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {itemsPerPageOptions.map((option) => (
              <SelectItem key={option} value={option.toString()}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="hidden sm:inline">
          {miscLabels.of} {totalItems}
        </span>
        <span className="sm:hidden text-[10px]">{totalItems}</span>
      </div>

      <div className="flex items-center gap-0.5 order-3 sm:order-2 flex-1 justify-center sm:flex-none">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="h-6 w-6 sm:h-7 sm:w-7 p-0 border-muted"
        >
          <ChevronsLeft className="h-3 w-3" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="h-6 w-6 sm:h-7 sm:w-7 p-0 border-muted"
        >
          <ChevronLeft className="h-3 w-3" />
        </Button>

        {visiblePages.map((page, index) => (
          <span key={index}>
            {page === "..." ? (
              <span className="px-0.5 sm:px-1 text-muted-foreground text-[10px] sm:text-xs">
                ...
              </span>
            ) : (
              <Button
                variant={currentPage === page ? "default" : "outline"}
                size="sm"
                onClick={() => onPageChange(page as number)}
                className="h-6 w-6 sm:h-7 sm:w-7 p-0 text-[10px] sm:text-xs border-muted min-w-[24px] sm:min-w-[28px]"
              >
                {page}
              </Button>
            )}
          </span>
        ))}

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="h-6 w-6 sm:h-7 sm:w-7 p-0 border-muted"
        >
          <ChevronRight className="h-3 w-3" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="h-6 w-6 sm:h-7 sm:w-7 p-0 border-muted"
        >
          <ChevronsRight className="h-3 w-3" />
        </Button>
      </div>

      <div className="text-[10px] sm:text-xs text-muted-foreground order-2 sm:order-3">
        <span className="sm:hidden">
          {currentPage}/{totalPages}
        </span>
        <span className="hidden sm:inline">
          {paginationLabels.pageOf
            .replace("{currentPage}", currentPage.toString())
            .replace("{totalPages}", totalPages.toString())}
        </span>
      </div>
    </div>
  );
};
